import { Box } from "@mui/material";
import { useId, type ReactNode } from "react";
import {
  SITE_COLOR,
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_TYPE,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { replayOnEnterRef } from "./motion/reveal";
import { useFadeOutOnLeave } from "./motion/useFadeOutOnLeave";
import { riseOrder, SITE_HERO_RISE_STYLES } from "./siteHeroStyles";
import { SiteTitle } from "./SiteText";

/** 가로 터치 화면 첫 화면의 위·아래 여백 — 그림 높이는 머리말 아래 화면 높이에서 이만큼 뺀 값까지 */
const LANDSCAPE_PAD_TOP = "12px";
const LANDSCAPE_PAD_BOTTOM = "20px";

const PAGE_HERO_STYLES = {
  section: {
    paddingTop: "clamp(64px,12vw,176px)",
    // 가로 터치 화면: 머리말 아래 한 화면 안에 왼쪽 글 묶음 0.8 : 오른쪽 그림 1.3 — 글이 한 화면을 다 쓰고 그림이 다음 화면으로 밀리지 않게
    [SITE_MEDIA.landscapeTouch]: {
      boxSizing: "border-box",
      minHeight: `calc(100svh - ${SITE_HEADER_HEIGHT_PX}px)`,
      display: "grid",
      gridTemplateColumns: "minmax(0,.8fr) minmax(0,1.3fr)",
      alignItems: "center",
      columnGap: "24px",
      padding: `${LANDSCAPE_PAD_TOP} var(--gutter) ${LANDSCAPE_PAD_BOTTOM}`,
    },
  },
  head: {
    ...SITE_WRAP,
    ...SITE_HERO_RISE_STYLES,
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) minmax(0,420px)",
    gap: "clamp(32px,6vw,96px)",
    alignItems: "end",
    marginBottom: "clamp(36px,4.6vw,60px)",
    [SITE_MEDIA.tablet]: {
      gridTemplateColumns: "1fr",
      alignItems: "start",
      gap: "24px",
    },
    // 가로 터치 화면: 글 묶음은 왼쪽 칸에 한 열로(머리말·제목·설명·버튼), 그 높이에 맞게 작게
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "1fr",
      alignItems: "start",
      gap: 0,
      margin: 0,
      padding: 0,
      maxWidth: "none",
      "& h1": { fontSize: "26px", lineHeight: 1.25, marginBottom: "12px" },
    },
  },
  eyebrow: {
    ...SITE_TYPE.eyebrow,
    color: SITE_COLOR.ink3,
    marginBottom: "clamp(20px,2.6vw,36px)",
    [SITE_MEDIA.landscapeTouch]: { marginBottom: "8px" },
  },
  description: {
    fontSize: "clamp(16px,1.3vw,18px)",
    lineHeight: 1.7,
    color: SITE_COLOR.ink2,
    marginBottom: "28px",
    [SITE_MEDIA.landscapeTouch]: {
      fontSize: "14px",
      lineHeight: 1.55,
      marginBottom: "12px",
    },
  },
  actions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  /** 그림 칸 틀 — 가로 터치 화면에서는 오른쪽 칸을 꽉 채우고 그림 높이를 화면 안으로 */
  figureWrap: {
    ...SITE_WRAP,
    [SITE_MEDIA.landscapeTouch]: {
      padding: 0,
      maxWidth: "none",
      width: "100%",
      "& > *": {
        maxHeight: `calc(100svh - ${SITE_HEADER_HEIGHT_PX}px - ${LANDSCAPE_PAD_TOP} - ${LANDSCAPE_PAD_BOTTOM})`,
        margin: 0,
      },
    },
  },
  /** 큰 둥근 그림(시안 `.phero-fig`) — 첫 화면에서 살짝 크게 시작해 제자리로 */
  figure: {
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    aspectRatio: "1224/600",
    background: "#C9D3DB",
    position: "relative",
    "& > img": { width: "100%", height: "100%", objectFit: "cover" },
    "& img": { animation: "siteSettle 1.6s cubic-bezier(.2,.7,.2,1) both" },
    [SITE_MEDIA.phone]: { aspectRatio: "4/5" },
    // 가로 터치 화면: 높이 상한은 틀(figureWrap)이 건다 — 비율을 지키며 폭이 함께 줄어 칸 가운데에 둔다
    [SITE_MEDIA.landscapeTouch]: { marginInline: "auto" },
  },
} as const;

export interface PageHeroProps {
  eyebrow: string;
  /** 줄바꿈 태그(`<br/>` 등)가 든 번역 문구 */
  title: string;
  description: string;
  /** 설명 아래 버튼 묶음 */
  actions: ReactNode;
  /** 큰 둥근 그림 칸에 들어갈 내용(보통 첫 화면 우선 `SiteImage`) */
  media: ReactNode;
  /** 그림 칸 대신 쓸 틀(플랫폼 첫 화면의 3D 화면 영상 합성처럼 칸 모양이 다를 때) */
  figure?: ReactNode;
}

/**
 * 서비스 페이지 첫 화면(시안 `.phero`): 작은 머리말·큰 제목(왼쪽) + 설명·버튼(오른쪽) → 큰 둥근 그림.
 * 글은 차례로 떠오르고, 화면 위로 빠져나가는 동안 옅어진다.
 */
export function PageHero({
  eyebrow,
  title,
  description,
  actions,
  media,
  figure,
}: PageHeroProps) {
  const titleId = useId();
  const headRef = useFadeOutOnLeave<HTMLDivElement>();
  return (
    <Box
      component="section"
      sx={PAGE_HERO_STYLES.section}
      aria-labelledby={titleId}
    >
      <Box
        ref={(element: HTMLDivElement | null) => {
          headRef.current = element;
          return replayOnEnterRef(element);
        }}
        sx={PAGE_HERO_STYLES.head}
      >
        <div>
          <Box component="p" sx={PAGE_HERO_STYLES.eyebrow}>
            {eyebrow}
          </Box>
          <Box component="h1" id={titleId} sx={SITE_TYPE.h1}>
            <SiteTitle text={title} />
          </Box>
        </div>
        <div style={riseOrder(1)}>
          <Box component="p" sx={PAGE_HERO_STYLES.description}>
            {description}
          </Box>
          <Box sx={PAGE_HERO_STYLES.actions}>{actions}</Box>
        </div>
      </Box>
      <Box sx={PAGE_HERO_STYLES.figureWrap}>
        {figure ?? (
          <Box component="figure" sx={PAGE_HERO_STYLES.figure}>
            {media}
          </Box>
        )}
      </Box>
    </Box>
  );
}
